import React, { useRef, useMemo, useState, useEffect, useCallback, Suspense } from 'react';
import _get from 'lodash/get';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import uuid from 'uuid';
import {
  AWS_REGION,
  AWS_SUFFIX,
  STATIC_ASSETS_BUCKET,
  IS_FEDERAL,
} from '@scale/frontend-lib/constants';
import { makeStyles } from '@material-ui/core';
import { Alert } from 'design-system';
import NoSsr from '@material-ui/core/NoSsr';
import IsomorphicLink from 'react-scale/corp/components/UI/IsomorphicLink';
import { urlHostIsSupported } from '@scale/shared/corp/courses/diff';
import cx from 'classnames';

const REMOTASKS_STATIC_CDN = IS_FEDERAL
  ? `https://${STATIC_ASSETS_BUCKET}.s3-${AWS_REGION}.${AWS_SUFFIX}`
  : 'https://static.remotasks.com';

// The Quill library relies heavily on DOM APIs and SSR will crash the app, so use dynamic import to solve the problem.
const ReactQuill = React.lazy(() => import('./WrappedReactQuill'));

interface ForwardedRefReactQuillProps {
  onChange: (_: string) => void;
  value: string;
  theme: string;
  formats?: string[];
  modules?: Record<any, any>;
}

const ForwardedRefReactQuill = React.forwardRef<HTMLDivElement, ForwardedRefReactQuillProps>(
  function ForwardedRefReactQuill(props, ref) {
    return (
      <NoSsr defer={false}>
        <Suspense fallback={<></>}>
          {/* @ts-ignore */}
          <ReactQuill quillRef={ref} {...props} />
        </Suspense>
      </NoSsr>
    );
  },
);

const RichTextEditor = ({
  onChange,
  value,
  options = {},
  enableGrammarChecking = false,
  className,
  customModules,
  ignoreBadLinks = false,
  ...props
}: {
  onChange: (_: string) => void;
  value: string;
  options: { uploadImgBase?: string; disableImageUpload?: boolean; useRapidEndpoint?: boolean };
  className?: string;
  enableGrammarChecking?: boolean;
  customModules?: Record<any, any>;
  ignoreBadLinks?: boolean;
}) => {
  const classes = useStyles();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [id, setId] = useState<string>('');
  const quillRef = useRef<HTMLDivElement>(null);
  const [Sapling, setSapling] = useState<any>(null);
  const { uploadImgBase, disableImageUpload } = options;

  // I guess because RichTextEditor is not SSR, so we need to dynamically import the Sapling module
  useEffect(() => {
    import('@scale/frontend-lib/sapling-module')
      .then(mod => {
        setSapling(mod.default);
        return;
      })
      .catch(_ => {
        setSapling(null);
      });
  }, []);

  useEffect(() => {
    const editor = document.querySelector('.ql-editor') as HTMLElement;

    if (editor && Sapling) {
      editor.setAttribute('contenteditable', 'true');
      editor.setAttribute('data-gramm', 'false');
      if (enableGrammarChecking && value.length < 40000) {
        Sapling.observe(editor);
      } else {
        editor.setAttribute('spellcheck', 'false');
        Sapling.unobserve(editor);
      }
    }
  });

  useEffect(() => {
    const newId = uuid.v4();
    setId(newId);
  }, []);

  const badLinks: string[] = useMemo(() => {
    if (!value) return [];
    // Match image links in Markdown, taken from https://stackoverflow.com/questions/44227270/regex-to-parse-image-link-in-markdown
    const markdownLinks = [...value.matchAll(/!\[[^\]]*\]\((.*?)\s*("(?:.*[^"])")?\s*\)/g)].map(
      match => match[1],
    );
    const imgLinks = [...value.matchAll(/<img[^>]*src=['"]?(http[^'"\s]*)['"]?[^>]*\/?>/g)].map(
      m => m[1],
    );
    const badLinks = markdownLinks.concat(imgLinks).filter(link => !urlHostIsSupported(link));
    return badLinks;
  }, [value]);

  const uploadImgToS3 = useCallback(
    async ({ file, fileName }: any) => {
      const body = {
        folderName: uploadImgBase,
        fileName,
        overwrite: true,
        contentType: 'image/png',
      };

      try {
        setIsUploading(true);
        const { url, fields } = await apiFetch(
          null,
          options.useRapidEndpoint
            ? 'internal/self_serve/courses/upload_static_asset_to_s3'
            : 'corp-api/courses/upload_static_asset_to_s3',
          {
            method: 'POST',
            body,
          },
        );

        // post to S3
        const formData = new FormData();
        for (const [k, v] of Object.entries(fields)) {
          formData.append(k, v as string);
        }
        formData.append('file', file);
        await fetch(url, { method: 'POST', body: formData });
        setIsUploading(false);
      } catch (error) {
        setIsUploading(false);
        defaultApiFetchErrorHandler(error, { handleHttp: true });
      }
    },
    [setIsUploading, uploadImgBase],
  );

  // auto upload clipboard image
  useEffect(() => {
    const ele = document.querySelector('#rq-container-' + id) as HTMLElement;
    async function pastHandler(e: ClipboardEvent) {
      const clipboardData = e.clipboardData;
      const file = _get(clipboardData, ['files', '0']);
      if (file && file.type.startsWith('image') && !disableImageUpload) {
        const fileName = uuid.v4() + '/' + file.name;
        await uploadImgToS3({ file, fileName });
        const imgEle = document.querySelector("img[src*='base64']");
        const relativeUrl = uploadImgBase + '/' + fileName;
        const url = new URL(relativeUrl, REMOTASKS_STATIC_CDN).toString();
        imgEle?.setAttribute('src', url);
      }
    }
    ele?.addEventListener('paste', pastHandler);
    return () => ele?.removeEventListener('paste', pastHandler);
  }, [id, uploadImgToS3, uploadImgBase, disableImageUpload]);

  // auto upload import image file
  const imageHandler = () => {
    const input = document.createElement('input') as HTMLInputElement;

    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file: any = _get(input, ['files', '0']);
      const fileName = file.name;
      if (quillRef.current) {
        // @ts-ignore
        const range = quillRef.current.getEditorSelection();
        await uploadImgToS3({ file, fileName });
        const relativeUrl = uploadImgBase + '/' + fileName;
        const url = new URL(relativeUrl, REMOTASKS_STATIC_CDN).toString();
        // @ts-ignore
        quillRef.current.getEditor().insertEmbed(range.index, 'image', url);
      }
    };
  };

  // when user input url with mp3 suffix, generate <auido/> tag instead of <video/>
  const videoHandler = () => {
    const url = prompt('Enter Video/Mp3 URL: ');
    if (!url) {
      return;
    }

    if (quillRef.current) {
      // @ts-ignore
      const range = quillRef.current.getEditorSelection();

      if (url.endsWith('mp3')) {
        const rangePlusOne = range?.index ? range?.index : 1; // need to plus one otherwise browser crashes
        // @ts-ignore
        quillRef.current.getEditor().insertEmbed(rangePlusOne, 'audio', url, 'user');
      } else {
        // @ts-ignore
        quillRef.current.getEditor().insertEmbed(range.index, 'video', url);
      }
    }
  };

  const modules = useMemo(
    () =>
      customModules || {
        toolbar: {
          handlers: { image: imageHandler, video: videoHandler },
          container: [
            [{ header: '1' }, { header: '2' }, { font: [] }],
            [{ size: [] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ align: [] }],
            [{ color: [] }, { background: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
            disableImageUpload
              ? ['link', 'video', 'code-block']
              : ['link', 'video', 'image', 'code-block'],
            ['clean'],
          ],
        },
        imageResize: {
          handleStyles: {
            displaySize: true,
          },
          modules: ['Resize', 'DisplaySize'],
        },
        clipboard: {
          matchVisual: false,
        },
      },
    [],
  );

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'color',
    'background',
    'list',
    'bullet',
    'indent',
    'link',
    'video',
    'image',
    'code-block',
    'align',
    'width',
    'audio',
  ];

  if (!uploadImgBase && !disableImageUpload) {
    return (
      <Alert severity="error">
        to show rich text editor, you have to config uploadImgBase in options params
      </Alert>
    );
  }
  return (
    <>
      <div id={'rq-container-' + id} className={cx(classes.container, className)}>
        {isUploading && (
          <div className={classes.loadingOverlay}>
            <span>uploading image...</span>
          </div>
        )}
        <ForwardedRefReactQuill
          onChange={onChange}
          value={value}
          theme="snow"
          modules={modules}
          formats={formats}
          ref={quillRef}
          {...props}
        />
      </div>
      {!!badLinks.length && !ignoreBadLinks && (
        <div style={{ overflow: 'scroll' }}>
          <Alert severity="warning">
            <div>
              We recommend using our{' '}
              <IsomorphicLink href={'/v2/upload_files_to_s3'}>internal bucket</IsomorphicLink> for
              your embedded content. You should fix the following embed links:
            </div>
            <ul>
              {badLinks.map((link, i) => (
                <li key={i} style={{ wordBreak: 'break-all', maxWidth: '300px' }}>
                  {link}
                </li>
              ))}
            </ul>
          </Alert>
        </div>
      )}
    </>
  );
};

const useStyles = makeStyles({
  richText: {
    padding: '0 !important',
    height: 'auto !important',
  },
  container: {
    position: 'relative',
    width: '100%',
    maxHeight: '65vh',
    display: 'flex',
    '& .ql-container': {
      flex: 1,
      overflow: 'scroll',
    },
    '.ql-snow': {
      border: 'none',
      overflow: 'scroll',
    },

    '& > div ': {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
  },
  loadingOverlay: {
    position: 'absolute',
    background: 'rgba(210, 215, 211, .5)',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& > span': {
      padding: 10,
      background: 'white',
      borderRadius: 10,
      fontSize: 15,
      fontWeight: 'bold',
    },
  },
});

const RichTextPreview = ({ content }: { content: string }) => {
  const classes = useStyles();
  return (
    <div
      className={`${classes.richText} ql-editor`}
      dangerouslySetInnerHTML={{ __html: content || '' }}
    ></div>
  );
};
export { RichTextEditor, RichTextPreview };
